import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import SectionHeading from '../components/ui/SectionHeading';
import SafeIcon from '../common/SafeIcon';
import { caseStudies, personalInfo } from '../data/content';

const Work = () => {
  const [filter, setFilter] = useState('All');
  const categories = ['All', 'Brand Strategy', 'AI Orchestration', 'Operations'];
  const filteredStudies = filter === 'All' ? caseStudies : caseStudies.filter(study => study.category === filter);

  return (
    <div className="pt-24 min-h-screen bg-white dark:bg-brand-dark transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <SectionHeading title="Case Studies" subtitle="Strategic Portfolio" />
        
        {/* Interactive Filter */}
        <div className="flex flex-wrap gap-4 mb-16">
          {categories.map((cat) => (
            <button 
              key={cat} 
              onClick={() => setFilter(cat)}
              className={`px-6 py-2 rounded-full text-xs uppercase font-bold tracking-widest transition-all duration-300 ${filter === cat ? 'bg-brand-red text-white shadow-lg shadow-brand-red/30' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-24 md:gap-32">
          <AnimatePresence mode="wait">
            {filteredStudies.map((study, index) => (
              <motion.div 
                key={study.id}
                layout
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.6 }}
                className={`flex flex-col ${index % 2 !== 0 ? 'md:flex-row-reverse' : 'md:flex-row'} gap-12 lg:gap-24 items-center`}
              >
                {/* Image Side */}
                <Link to={`/work/${study.id}`} className="w-full md:w-1/2 relative group rounded-2xl overflow-hidden shadow-xl block">
                  <div className="absolute inset-0 bg-brand-cyan mix-blend-multiply opacity-20 group-hover:opacity-0 transition-opacity duration-500 z-10"></div>
                  <div className="aspect-[4/3] bg-slate-100 dark:bg-slate-800">
                    <img src={study.image} alt={study.title} className="w-full h-full object-cover filter grayscale-[20%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
                  </div>
                </Link>

                {/* Content Side */}
                <div className="w-full md:w-1/2">
                  <span className="inline-block px-3 py-1 bg-brand-yellow/20 dark:bg-brand-yellow/30 text-yellow-800 dark:text-brand-yellow text-[10px] font-bold uppercase tracking-widest rounded-full mb-6 border border-brand-yellow/30">
                    {study.category}
                  </span>
                  <Link to={`/work/${study.id}`}>
                    <h3 className="text-3xl md:text-5xl font-heading font-bold text-brand-dark dark:text-white mb-4 leading-tight hover:text-brand-red dark:hover:text-brand-cyan transition-colors">
                      {study.title}
                    </h3>
                  </Link>
                  <p className="text-sm font-bold text-slate-500 mb-6 uppercase tracking-wider">
                    Client: <span className="text-brand-red dark:text-brand-cyan">{study.client}</span>
                  </p>
                  <p className="text-slate-600 dark:text-slate-400 font-medium leading-relaxed mb-8 text-lg">
                    {study.summary}
                  </p>
                  <div className="space-y-4 relative before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-brand-red dark:before:bg-brand-yellow before:rounded pl-6 bg-slate-50 dark:bg-slate-900/50 p-6 rounded-r-xl border border-slate-100 dark:border-slate-800 border-l-0">
                    <h4 className="text-xs uppercase tracking-widest text-brand-dark dark:text-white font-bold mb-4">Strategic Impact</h4>
                    {study.results.map((result, rIndex) => (
                      <div key={rIndex} className="flex items-center gap-3">
                        <SafeIcon name="CheckCircle" className="w-5 h-5 text-brand-red dark:text-brand-cyan shrink-0" />
                        <span className="text-slate-700 dark:text-slate-300 font-medium">{result}</span>
                      </div>
                    ))}
                  </div>

                  <Link to={`/work/${study.id}`} className="mt-8 inline-flex items-center gap-2 text-xs uppercase font-bold tracking-widest text-brand-red dark:text-brand-cyan hover:opacity-80 transition-opacity group">
                    Read Full Case Study <SafeIcon name="ArrowRight" className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* SEVEN HATS CTA */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-32 p-12 text-center bg-brand-dark dark:bg-brand-darker text-white rounded-3xl relative overflow-hidden border border-brand-cyan/20"
        >
          <div className="absolute inset-0 opacity-[0.2]" style={{ backgroundImage: 'linear-gradient(to right, #40E0D0 1px, transparent 1px), linear-gradient(to bottom, #40E0D0 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
          <div className="relative z-10">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon name="Shield" className="w-8 h-8 text-brand-yellow" />
            </div>
            <h3 className="text-3xl font-heading font-bold mb-4">Seeking Fractional Leadership?</h3>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto font-medium text-lg">
              I utilize the SEVEN HATS® intelligent platform to scale your marketing operations and elevate your brand positioning with data-driven precision.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="tel:+14047939923" 
                className="inline-flex items-center gap-2 px-8 py-4 bg-brand-cyan text-brand-dark text-sm uppercase font-bold tracking-widest hover:bg-white transition-colors rounded-lg shadow-xl shadow-brand-cyan/10"
              >
                Call or Message Me <SafeIcon name="MessageSquare" className="w-4 h-4" />
              </a>
              <a 
                href={personalInfo.calendar}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-brand-dark text-sm uppercase font-bold tracking-widest hover:bg-brand-yellow transition-colors rounded-lg shadow-xl"
              >
                <SafeIcon name="Calendar" className="w-4 h-4" /> Book a 15 minute call
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Work;